import { Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DeliverableCard } from '@/components/layout/DeliverableCard/DeliverableCard';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import {
  CONTINUE_ONBOARDING_FLOW_PARAM,
  ENTITY_INTAKE_STEP,
} from '@/modules/createHousehold/CreateHouseholdFlow/CreateHouseholdFlow';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { ROUTE_KEYS } from '@/navigation/constants';
import { WorkflowTrackerKind } from '@/types/schema';

import { ClientOnboardingDeliverableModalBodyProps } from '../../types';
import { GenericDocumentProcessingModalBody } from './GenericDocumentProcessingModalBody';

function CreateEntityOverviewModalBodyInner({
  onClose,
  onChildPhaseChange,
}: ClientOnboardingDeliverableModalBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { uploadedFiles, selectedExistingDocuments } =
    useMultiDocumentUploaderContext();

  const handleManualEntityCreation = useCallback(() => {
    // Close the ClientOnboardingDeliverableModal
    onClose();

    // Then set the search params open the CreateEntityShortFormModal
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(CONTINUE_ONBOARDING_FLOW_PARAM, ENTITY_INTAKE_STEP);
    setSearchParams(nextSearchParams, { replace: true });
  }, [onClose, searchParams, setSearchParams]);

  const additionalUploadContent =
    isEmpty(uploadedFiles) && isEmpty(selectedExistingDocuments) ? (
      <>
        <Stack direction="row" sx={{ justifyContent: 'center' }}>
          <Typography variant="body-xs">or</Typography>
        </Stack>
        <DeliverableCard
          title="Add entities manually"
          description="Create entities manually without AI suggestions"
          sizeVariant="small"
          onClick={handleManualEntityCreation}
        />
      </>
    ) : null;

  const config = {
    allowSelectExistingDocuments: false,
    subheader:
      'Luminary AI will process each document, create entities where applicable and summarize the entities into a client-facing deliverable that you can review.',
    workflowTrackerKind: WorkflowTrackerKind.DocumentExtraction,
    useAIWorkflow: true,
    processingHeader: (documentNoun: string) => `Processing ${documentNoun}...`,
    processingSubheader: (
      <>
        We are processing your documents and will notify you when entity
        suggestions are ready for review.
        <strong> This process may take up to several minutes.</strong>
      </>
    ),
    successHeader: 'Success! Entity suggestions ready',
    successSubheader: 'Your entity suggestions are ready for review',
    errorMessage:
      'We failed to extract entity suggestions from these documents. Please upload new documents or re-select the previous document to try again.',
    completionButtonLabel: 'Continue to trusts',
    onCompletionClick: (
      navigate: ReturnType<typeof useNavigateToRoute>['navigate'],
      householdId: string
    ) => {
      navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, {
        householdId,
      });
    },
    additionalUploadContent,
  };

  return (
    <GenericDocumentProcessingModalBody
      onClose={onClose}
      onChildPhaseChange={onChildPhaseChange}
      config={config}
    />
  );
}

export function CreateEntityOverviewModalBody(
  props: ClientOnboardingDeliverableModalBodyProps
) {
  return (
    <MultiDocumentUploaderProvider>
      <CreateEntityOverviewModalBodyInner {...props} />
    </MultiDocumentUploaderProvider>
  );
}
